import React from 'react';
import { ErrorBoundary } from '@sentry/react';
import { Button } from '@ui';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface ErrorFallbackProps {
  error: unknown;
  resetError: () => void;
  eventId: string | null;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetError, eventId }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
        <div className="flex justify-center mb-4">
          <AlertTriangle className="h-12 w-12 text-red-500" />
        </div>
        
        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          ¡Oops! Algo salió mal
        </h1>
        
        <p className="text-gray-600 mb-4">
          Ha ocurrido un error inesperado. Nuestro equipo ha sido notificado automáticamente.
        </p>
        
        {eventId && (
          <p className="text-sm text-gray-500 mb-4">
            ID del error: {eventId}
          </p>
        )}
        
        <div className="space-y-3">
          <Button 
            onClick={resetError}
            className="w-full"
            variant="default"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Intentar de nuevo
          </Button>
          
          <Button 
            onClick={() => window.location.reload()}
            variant="outline"
            className="w-full"
          >
            Recargar página
          </Button>
        </div>
        
        {process.env.NODE_ENV === 'development' && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
              Detalles del error (solo desarrollo)
            </summary>
            <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto">
              {error instanceof Error ? error.stack : String(error)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

interface SentryErrorBoundaryProps {
  children: React.ReactNode;
}

export const SentryErrorBoundary: React.FC<SentryErrorBoundaryProps> = ({ 
  children 
}) => {
  return (
    <ErrorBoundary
      fallback={({ error, resetError, eventId }) => (
        <ErrorFallback error={error} resetError={resetError} eventId={eventId} />
      )}
      beforeCapture={(scope, error, errorInfo) => {
        scope.setTag('errorBoundary', 'true');
        scope.setContext('errorInfo', {
          errorInfo: String(errorInfo),
        });
        console.error('Error captured by boundary:', error);
      }}
      showDialog={false}
    >
      {children}
    </ErrorBoundary>
  );
};
